import { TableColumn, Badge, utils, Table } from "@vilocnv/allsetra-core";
import { Box, Typography } from "@mui/material";
import { DemoObjectCarIcon } from "../../../../assets/icons";
import { useTranslation } from "react-i18next";

export const EXCEPTION_REPORT_TABLE_COLUMNS = (
  translator: any
): TableColumn<any>[] => [
  {
    name: translator("tableHeading.rideId", {
      ns: "tableHeadingsTranslation"
    }),
    selector: (row: any) => (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "12px"
        }}
      >
        <DemoObjectCarIcon />
        <Typography
          sx={{ fontSize: "14px", fontWeight: 500, color: "#323947" }}
        >
          {row?.vehicleInfo?.type || "N/A"} (License Plate:{" "}
          {row?.vehicleInfo?.licensePlate || "N/A"})
        </Typography>
      </Box>
    ),
    sortable: false,
    width: "350px"
  },
  {
    name: translator("tableHeading.start", {
      ns: "tableHeadingsTranslation"
    }),
    selector: (row: any) => "",
    sortable: false,
    width: "200px"
  },
  {
    name: translator("tableHeading.end", {
      ns: "tableHeadingsTranslation"
    }),
    selector: (row: any) => "",
    sortable: false,
    width: "200px"
  },
  {
    name: translator("tableHeading.startMileage", {
      ns: "tableHeadingsTranslation"
    }),
    selector: (row: any) => "",
    sortable: false,
    width: "180px"
  },
  {
    name: translator("tableHeading.endMileage", {
      ns: "tableHeadingsTranslation"
    }),
    selector: (row: any) => "",
    sortable: false,
    width: "180px"
  }
];

export const EXCEPTION_REPORT_RIDE_COLUMNS = (
  translator: any
): TableColumn<any>[] => [
  {
    name: translator("tableHeading.rideId", {
      ns: "tableHeadingsTranslation"
    }),
    selector: (row: any) => row?.rideId || "N/A",
    sortable: true,
    width: "380px"
  },
  {
    name: translator("tableHeading.start", {
      ns: "tableHeadingsTranslation"
    }),
    selector: (row: any) =>
      row?.startDate
        ? utils.formatDate({
            date: row.startDate,
            dateFormat: "dd-MMM-yyyy, HH:mm"
          })
        : "N/A",
    sortable: true,
    width: "200px"
  },
  {
    name: translator("tableHeading.end", {
      ns: "tableHeadingsTranslation"
    }),
    selector: (row: any) =>
      row?.endDate
        ? utils.formatDate({
            date: row.endDate,
            dateFormat: "dd-MMM-yyyy, HH:mm"
          })
        : "N/A",
    sortable: true,
    width: "200px"
  },
  {
    name: translator("tableHeading.startMileage", {
      ns: "tableHeadingsTranslation"
    }),
    selector: (row: any) =>
      row?.startMileage ? `${row.startMileage.toLocaleString()} km` : "N/A",
    sortable: true,
    width: "180px"
  },
  {
    name: translator("tableHeading.endMileage", {
      ns: "tableHeadingsTranslation"
    }),
    selector: (row: any) =>
      row?.endMileage ? `${row.endMileage.toLocaleString()} km` : "N/A",
    sortable: true,
    width: "180px"
  }
];

export const ExceptionReportVehicleExpandableRow = ({
  data
}: {
  data: any;
}) => {
  const { t } = useTranslation(["translation", "tableHeadingsTranslation"]);

  if (!data.rides || data.rides.length === 0) {
    return (
      <Box sx={{ padding: "16px", textAlign: "center", color: "#646D7B" }}>
        No rides found for this vehicle.
      </Box>
    );
  }

  return (
    <Table
      columns={EXCEPTION_REPORT_RIDE_COLUMNS(t)}
      data={data.rides}
      expandableRows
      expandOnRowClicked
      expandableRowsComponent={ExceptionReportRideExpandableRow}
      pagination={false}
      noTableHead={true}
      translator={t}
    />
  );
};

export const ExceptionReportRideExpandableRow = ({ data }: { data: any }) => {
  if (!data.incompleteFields || data.incompleteFields.length === 0) {
    return null;
  }

  return (
    <Box sx={{ backgroundColor: "#f8f9fa", width: "100%" }}>
      {/* Unique headers row with same style as main headers */}
      <Box
        sx={{
          display: "flex",
          backgroundColor: "#f9fbff",
          color: "#959eb3",
          textTransform: "uppercase",
          fontSize: "12px",
          fontWeight: 500,
          letterSpacing: "1px",
          lineHeight: "132%",
          minHeight: "32px",
          alignItems: "center",
          borderBottom: "1px solid #e0e0e0",
          width: "100%"
        }}
      >
        <Box sx={{ width: "250px", paddingLeft: "72px" }}>INCOMPLETE FIELD</Box>
        <Box sx={{ width: "380px", paddingLeft: "6px" }}>TECHNICAL CAUSE</Box>
        <Box sx={{ width: "410px", paddingLeft: "6px" }}>WARNING</Box>
      </Box>

      {/* Data rows */}
      <Box
        sx={{
          backgroundColor: "#FFF"
        }}
      >
        {data.incompleteFields.map((field: any, index: number) => (
          <Box
            key={index}
            sx={{
              display: "flex",
              paddingY: "8px",
              marginBottom:
                index < data.incompleteFields.length - 1 ? "16px" : 0,
              borderBottom: "1px solid #e0e0e0",
              alignItems: "flex-start",
              width: "100%"
            }}
          >
            <Box
              sx={{ width: "250px", paddingLeft: "72px", paddingRight: "24px" }}
            >
              <Typography
                sx={{ fontSize: "14px", fontWeight: 500, color: "#323947" }}
              >
                {field.field}
              </Typography>
            </Box>
            <Box
              sx={{ width: "380px", paddingLeft: "6px", paddingRight: "24px" }}
            >
              <Typography sx={{ fontSize: "14px", color: "#323947" }}>
                {field.technicalCause}
              </Typography>
            </Box>
            <Box
              sx={{ width: "410px", paddingLeft: "6px", paddingRight: "24px" }}
            >
              <Typography sx={{ fontSize: "14px", color: "#323947" }}>
                {field.warning}
              </Typography>
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
};
